// src/components/SearchAddBar.jsx
import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

const SearchAddBar = forwardRef(({
  searchTerm,
  setSearchTerm,
  onAdd,
  onFocus,
  onClear,
  isSaving = false,
  autoFocus = false,
  placeholder,
}, ref) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder || t('search.placeholder');
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
  const inputRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    clear: () => {
      setLocalSearchTerm('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  useEffect(() => {
    setLocalSearchTerm(searchTerm || '');
  }, [searchTerm]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    setSearchTerm(value);
  };

  const handleClear = () => {
    setLocalSearchTerm('');
    setSearchTerm('');
    if (onClear) onClear();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFocus = () => {
    if (onFocus) onFocus();
  };

  const handleAddClick = () => {
    if (localSearchTerm.trim() && !isSaving && onAdd) {
      onAdd(localSearchTerm.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && localSearchTerm.trim() && !isSaving) {
      handleAddClick();
    }
  };

  const isAddEnabled = localSearchTerm.trim() !== '' && !isSaving;

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <input
          ref={inputRef}
          type="text"
          value={localSearchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyPress={handleKeyPress}
          placeholder={resolvedPlaceholder}
          disabled={isSaving}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
        />

        {localSearchTerm && !isSaving && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
            type="button"
            aria-label={t('search.clearSearch')}
          >
            <i className="fas fa-times text-gray-600"></i>
          </button>
        )}
      </div>

      <button
        onClick={handleAddClick}
        disabled={!isAddEnabled}
        className={`px-4 py-2 font-medium rounded-lg transition-colors whitespace-nowrap ${
          isAddEnabled
            ? 'bg-purple-600 text-white hover:bg-purple-700'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isSaving ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {t('common.adding')}
          </div>
        ) : (
          <>
            <i className="fas fa-plus mr-2"></i>
            {t('common.add')}
          </>
        )}
      </button>
    </div>
  );
});

SearchAddBar.displayName = 'SearchAddBar';

export default SearchAddBar;
